// Scenario B: the Design tab. The requirements (drafted by the assistant,
// edited and confirmed by the person), the protocol it designs from them
// with the largest message sizes under the nanopb limits, the nanopb
// options, and the download for the firmware team.
import { useEffect, useState } from "preact/hooks";
import type { ComponentChildren } from "preact";
import { Icon } from "./icons";
import type { Actions, WorkbenchData } from "./model";
import type { ProjectDocument, SizeReport } from "../lib/types";
import { renderMarkdown } from "../lib/markdown";
import { fmtDateTime, fmtNumber, t } from "../i18n";

export const PROTOCOL_PROMPT =
  "The requirements are confirmed. Design the protocol: the .proto following the gIoTHub conventions and the nanopb options, " +
  "check the sizes against the radio's payload limit, then classify every field and message, generate samples, validate and write ai_guide.md.";

export const requirementsPrompt = (description: string) =>
  `This is the device:\n\n${description.trim()}\n\nRead the uploaded documents, write the requirements for me to confirm, and ask me about every open point.`;

const when = (iso?: string) => fmtDateTime(iso);

function Card({ title, right, children }: { title: string; right?: ComponentChildren; children: ComponentChildren }) {
  return (
    <section class="rounded-xl border border-line bg-side">
      <header class="flex min-h-[46px] flex-wrap items-center gap-2 border-b border-line px-4 py-2">
        <h3 class="m-0 text-[14px] font-semibold">{title}</h3>
        <span class="flex-1" />
        {right}
      </header>
      <div class="px-4 py-3">{children}</div>
    </section>
  );
}

/** Opens the requirements in a print window: the browser saves it as PDF. */
function printRequirements(data: WorkbenchData, d: ProjectDocument) {
  const w = window.open("", "_blank");
  if (!w) return;
  const status = d.status === "confirmed" ? t("design.print.confirmed", { version: d.version, when: when(d.confirmed_at) }) : t("design.print.draft", { version: d.version });
  w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${t("design.print.title", { name: data.project.name })}</title>
<style>body{font:15px/1.6 "IBM Plex Sans",system-ui,sans-serif;color:#111;max-width:760px;margin:40px auto;padding:0 24px}
h1{font-size:26px;margin:0 0 4px}h2{font-size:19px;border-bottom:1px solid #ccc;padding-bottom:4px;margin-top:28px}
code{font-family:"IBM Plex Mono",monospace;font-size:13px;background:#f2f2f2;padding:1px 4px;border-radius:3px}
table{border-collapse:collapse}td,th{border:1px solid #ccc;padding:4px 8px}.meta{color:#555;font-size:13px;margin-bottom:24px}</style></head>
<body><div class="meta">${data.project.name} · ${data.project.type_id} · ${data.me.org} · ${t("design.print.meta", { status })}</div>${renderMarkdown(d.content)}</body></html>`);
  w.document.close();
  w.focus();
  setTimeout(() => w.print(), 300);
}

export function DesignView({ data, actions, writer, onOpen }: { data: WorkbenchData; actions: Actions; writer: boolean; onOpen: (path: string) => void }) {
  const req = data.requirements;
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  const [desc, setDesc] = useState("");
  const [opts, setOpts] = useState<ProjectDocument | null>(null);
  const [optsText, setOptsText] = useState<string | null>(null);
  const [sizes, setSizes] = useState<SizeReport | null>(null);
  const [limit, setLimit] = useState("");
  const protoFiles = data.files.map((f) => f.path).filter((p) => p.startsWith("proto/") && p.endsWith(".proto"));

  useEffect(() => {
    actions.document("nanopb_options").then(setOpts).catch(() => {});
    actions.sizes().then(setSizes).catch(() => setSizes(null));
  }, [data.project.head_rev, data.live.running]);

  const act = async (what: string, fn: () => Promise<unknown>) => {
    setBusy(what);
    setErr("");
    try {
      await fn();
      return true;
    } catch (x) {
      setErr((x as Error).message);
      return false;
    } finally {
      setBusy("");
    }
  };
  const lim = parseInt(limit, 10) || 0;
  const confirmed = req?.status === "confirmed";
  const running = data.live.running;

  return (
    <div class="min-h-0 flex-1 overflow-y-auto">
      <div class="mx-auto flex max-w-[1100px] flex-col gap-5 px-6 py-6">
        <header>
          <h2 class="m-0 text-[19px] font-semibold">{t("design.title", { name: data.project.name })}</h2>
          <p class="mb-0 mt-1 text-muted">{t("design.intro")}
            {" "}<a href="/help/scenario-b" target="_blank">{t("design.manual")}</a>.</p>
        </header>
        {err && <p class="m-0 text-[13px] text-crit">{err}</p>}

        <Card title={t("design.req.title")} right={req && req.version > 0 && (
          <>
            <span class={`pill ${confirmed ? "bg-good-soft text-good" : "bg-warn-soft text-warn"}`}>
              {confirmed ? t("design.req.confirmed", { version: req.version }) : t("design.req.draft", { version: req.version })}
            </span>
            <span class="text-[12px] text-muted">{req.author_kind === "ai" ? t("design.by.ai") : t("design.by.person")} {when(req.updated_at)}</span>
            <button type="button" class="btn h-7 px-2 text-[12px]" onClick={() => printRequirements(data, req)} title={t("design.req.print.hint")}>{t("design.req.print")}</button>
            {writer && !editing && <button type="button" class="btn h-7 px-2 text-[12px]" disabled={running} onClick={() => { setText(req.content); setEditing(true); }}>{t("common.edit")}</button>}
            {writer && !confirmed && !editing && (
              <button type="button" class="btn btn-pri h-7 px-2.5 text-[12px]" disabled={!!busy || running}
                title={t("design.req.confirm.hint")}
                onClick={() => act("confirm", () => actions.confirmRequirements(req.version))}>
                <Icon name="check" size={13} stroke={2.4} />{busy === "confirm" ? t("design.req.confirming") : t("design.req.confirm")}
              </button>
            )}
          </>
        )}>
          {!req || req.version === 0 ? (
            <div class="flex flex-col gap-3">
              <p class="m-0 text-muted">{t("design.req.empty")}</p>
              {writer && (
                <>
                  <textarea class="field h-auto py-2 text-[13.5px] leading-[1.55]" rows={7} value={desc} disabled={running}
                    placeholder={t("design.req.placeholder")}
                    onInput={(e) => setDesc((e.target as HTMLTextAreaElement).value)} />
                  <div>
                    <button type="button" class="btn btn-pri" disabled={!desc.trim() || running || data.aiPaused || !!busy}
                      onClick={() => act("send", () => actions.send(requirementsPrompt(desc)))}>
                      <Icon name="spark" size={14} stroke={2} />{running ? t("design.working") : t("design.req.draftai")}
                    </button>
                  </div>
                </>
              )}
            </div>
          ) : editing ? (
            <div class="flex flex-col gap-2">
              <textarea class="field h-auto py-2 font-mono text-[12.5px] leading-[1.55]" rows={24} spellcheck={false} value={text}
                onInput={(e) => setText((e.target as HTMLTextAreaElement).value)} />
              <p class="m-0 text-[12px] text-muted">{t("design.req.edit.note")}</p>
              <div class="flex gap-2">
                <button type="button" class="btn btn-pri" disabled={!!busy || !text.trim()}
                  onClick={async () => { if (await act("save", () => actions.putDocument("requirements", text, req.version))) setEditing(false); }}>
                  {busy === "save" ? t("design.saving") : t("common.save")}
                </button>
                <button type="button" class="btn" onClick={() => setEditing(false)}>{t("common.cancel")}</button>
              </div>
            </div>
          ) : (
            <>
              {req.confirmed_version && !confirmed && (
                <details class="mb-3 rounded-lg border border-line2 px-3 py-2 text-[13px]">
                  <summary class="cursor-pointer text-warn">{t("design.req.older", { version: req.confirmed_version, when: when(req.confirmed_at) })}</summary>
                  <div class="help-prose mt-2 text-[13.5px]" dangerouslySetInnerHTML={{ __html: renderMarkdown(req.confirmed_content ?? "") }} />
                </details>
              )}
              <div class="help-prose max-w-none text-[14px]" dangerouslySetInnerHTML={{ __html: renderMarkdown(req.content) }} />
              {!confirmed && <p class="mb-0 mt-3 text-[13px] text-muted">{t("design.req.readthem")}</p>}
            </>
          )}
        </Card>

        <Card title={t("design.proto.title")} right={
          <>
            <label class="flex items-center gap-1.5 text-[12px] text-muted">{t("design.proto.limit")}
              <input class="field h-7 w-[84px] font-mono text-[12px]" inputMode="numeric" placeholder={t("design.proto.bytes")} value={limit}
                onInput={(e) => setLimit((e.target as HTMLInputElement).value.replace(/\D/g, ""))} />
            </label>
            <button type="button" class="btn h-7 px-2 text-[12px]" disabled={protoFiles.length === 0 || !!busy}
              title={t("design.proto.download.hint")}
              onClick={() => act("zip", async () => {
                const b = await actions.firmwareZip();
                const a = document.createElement("a");
                a.href = URL.createObjectURL(b);
                a.download = `${data.project.type_id}-r${data.project.head_rev}-protocol.zip`;
                a.click();
                setTimeout(() => URL.revokeObjectURL(a.href), 5000);
              })}>
              <Icon name="up" size={13} stroke={2.2} class="rotate-180" />{t("design.proto.download")}
            </button>
          </>
        }>
          {protoFiles.length === 0 ? (
            <div class="flex flex-col gap-3">
              <p class="m-0 text-muted">{confirmed ? t("design.proto.ready") : t("design.proto.after")}</p>
              {writer && confirmed && (
                <div>
                  <button type="button" class="btn btn-pri" disabled={running || data.aiPaused || !!busy}
                    onClick={() => act("send", () => actions.send(PROTOCOL_PROMPT))}>
                    <Icon name="spark" size={14} stroke={2} />{running ? t("design.working") : t("design.proto.design")}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div class="flex flex-col gap-3">
              <div class="flex flex-wrap gap-2">
                {protoFiles.map((p) => (
                  <button type="button" class="btn h-7 px-2 font-mono text-[12px]" onClick={() => onOpen(p)}><Icon name="file" size={13} stroke={2} />{p}</button>
                ))}
              </div>
              {sizes ? (
                <table class="w-full border-collapse text-[12.5px]">
                  <thead>
                    <tr class="text-left text-[11.5px] uppercase tracking-wide text-dim">
                      <th class="py-1.5 pr-3 font-semibold">{t("design.proto.col.message")}</th>
                      <th class="py-1.5 pr-3 text-right font-semibold">{t("design.proto.col.largest")}</th>
                      <th class="py-1.5 font-semibold">{t("design.proto.col.notes")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sizes.messages.map((m) => {
                      const root = m.message === sizes.root;
                      const over = root && lim > 0 && (!m.bounded || m.max_bytes > lim);
                      return (
                        <tr class="border-t border-line align-top">
                          <td class="py-1.5 pr-3 font-mono">{m.message}{root && <span class="pill ml-2 h-4 bg-sel px-1.5 text-accent2">{t("design.proto.root")}</span>}</td>
                          <td class={`py-1.5 pr-3 text-right font-mono ${over ? "text-crit" : ""}`}>{m.bounded ? `${fmtNumber(m.max_bytes)} B` : t("design.proto.unbounded")}{over && t("design.proto.over")}</td>
                          <td class="py-1.5 text-muted">
                            {(m.unbounded ?? []).map((u) => <span class="block text-warn">{u}</span>)}
                            {(m.callbacks ?? []).length > 0 && <span class="block">{t("design.proto.callbacks", { fields: m.callbacks!.join(", ") })}</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                <p class="m-0 text-muted">{t("design.proto.nosizes")}</p>
              )}
              {(sizes?.problems ?? []).map((p) => <p class="m-0 font-mono text-[12px] text-warn">{t("design.proto.problem", { problem: p })}</p>)}
              <p class="m-0 text-[12px] text-muted">{t("design.proto.note")}</p>
            </div>
          )}
        </Card>

        <Card title={t("design.opts.title")} right={opts && opts.version > 0 && (
          <span class="text-[12px] text-muted">{t("design.opts.meta", { version: opts.version, author: opts.author_kind === "ai" ? t("design.opts.by.ai") : t("design.opts.by.person"), when: when(opts.updated_at) })}</span>
        )}>
          <p class="mb-2 mt-0 text-[12.5px] text-muted">{t("design.opts.intro")} <code class="font-mono">package.Message.field max_size:64</code>,
            {" "}<code class="font-mono">max_count:16</code>, <code class="font-mono">type:FT_CALLBACK</code>. {t("design.opts.kept")}</p>
          <textarea class="field h-auto py-2 font-mono text-[12.5px] leading-[1.55]" rows={10} spellcheck={false} disabled={!writer || running}
            value={optsText ?? opts?.content ?? ""} onInput={(e) => setOptsText((e.target as HTMLTextAreaElement).value)} />
          {writer && optsText !== null && optsText !== (opts?.content ?? "") && (
            <div class="mt-2 flex gap-2">
              <button type="button" class="btn btn-pri" disabled={!!busy} onClick={async () => {
                if (await act("opts", async () => setOpts(await actions.putDocument("nanopb_options", optsText, opts?.version ?? 0)))) {
                  setOptsText(null);
                  setSizes(await actions.sizes());
                }
              }}>{busy === "opts" ? t("design.saving") : t("design.opts.save")}</button>
              <button type="button" class="btn" onClick={() => setOptsText(null)}>{t("design.opts.discard")}</button>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
